/**
 * Nearby-users widget data layer.
 *
 * Backed by `GET /user/list?type=nearest&lat=&lng=&limit=&nocount=Y` — the same
 * endpoint the legacy `<UserMap>` calls with `show_on_map=Y`. We pass `nocount`
 * so the response stays cheap (the right-rail card only needs the first ~5).
 *
 * The hook is opportunistic: when no `lat`/`lng` is supplied (e.g. the viewer
 * hasn't set `map_lat`/`map_lng` and we haven't asked for geolocation), the
 * request is skipped via `enabled:false` rather than firing with empty params.
 *
 * Failure & empty are both handled by the widget — never throws.
 */
import { useQuery } from '@tanstack/react-query';

import { api } from '@/lib/api/client';
import { queryKeys } from '@/lib/api/queryKeys';
import type { Paginated, User } from '@/types/api';

export interface NearbyUsersParams {
  lat?: number | null;
  lng?: number | null;
  /** Result cap — defaults to 5 (right-rail widget). */
  limit?: number;
  /** Max distance in km — passed through to the backend filter. */
  distance?: number;
}

/** Serialise the params into the `user/list` query string. */
function buildQuery(params: NearbyUsersParams): string {
  const search = new URLSearchParams();
  search.set('type', 'nearest');
  search.set('show_on_map', 'Y');
  search.set('nocount', 'Y');
  if (params.limit !== undefined) search.set('limit', String(params.limit));
  if (params.lat !== undefined && params.lat !== null) {
    search.set('lat', String(params.lat));
  }
  if (params.lng !== undefined && params.lng !== null) {
    search.set('lng', String(params.lng));
  }
  if (params.distance !== undefined) {
    search.set('distance', String(params.distance));
  }
  return `?${search.toString()}`;
}

/**
 * Nearby users for the right-rail "Who's near you" widget. Disabled until a
 * coordinate pair is provided so we don't query the backend with empty lat/lng.
 */
export function useNearbyUsers(params: NearbyUsersParams = {}) {
  const { lat, lng, limit = 5, distance } = params;
  const hasCoords =
    typeof lat === 'number' &&
    typeof lng === 'number' &&
    Number.isFinite(lat) &&
    Number.isFinite(lng);
  const queryParams = { lat, lng, limit, distance };
  return useQuery({
    queryKey: queryKeys.users.nearby(queryParams),
    queryFn: () =>
      api.get<Paginated<User>>(
        `user/list${buildQuery({ lat, lng, limit, distance })}`,
      ),
    enabled: hasCoords,
    staleTime: 60_000,
  });
}
